// User Management Component - Only visible to Admin/Manager users
import React, { useState, useEffect } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import PermissionGate from '../PermissionGate/PermissionGate';
import InvitationsPanel from './InvitationsPanel';
import InvitationCodeNotice from './InvitationCodeNotice';
import EmailIdentity from './EmailIdentity';
import { createInvitation, createResetCode, apiError } from './invitationsApi';
import './UserManagement.css';

// NOTE: Make Admin / Remove Admin actions were removed on purpose —
// Administrator group membership is managed manually in the Cognito console
// (Usage & Cost Tracking decision P8). The backend endpoints no longer exist.

// `embedded`: rendered inside another page (Account settings) that already provides the
// scrolling ground, so the page shell is skipped.
const UserManagement = ({ embedded = false }) => {
  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState(null);
  const [query, setQuery] = useState('');
  const [invitesVersion, setInvitesVersion] = useState(0);
  // Username whose code is being issued: a second click would issue another code
  // and silently invalidate the first.
  const [sendingCodeTo, setSendingCodeTo] = useState(null);

  const handleIssued = (invitation) => {
    setIssued(invitation);
    setInvitesVersion((v) => v + 1);
  };

  // Issues a code for `user` once at a time: a second click would issue another code
  // and silently invalidate the first.
  const issueFor = async (user, create) => {
    if (sendingCodeTo) return;
    setSendingCodeTo(user.username);
    try {
      handleIssued(await create(user.email));
    } catch (err) {
      alert(`Error: ${apiError(err)}`);
    } finally {
      setSendingCodeTo(null);
    }
  };

  // Accounts created before invitation codes still wait on a temporary password
  // that may never have arrived — give them a code instead.
  const handleSendCode = (user) => issueFor(user, createInvitation);

  // Finished accounts whose reset emails are blocked: a reset code they use at /signup?reset=1.
  const handleResetCode = (user) => issueFor(user, createResetCode);

  useEffect(() => {
    if (permissions?.canSeeUserManagement) {
      loadUsers();
    }
  }, [permissions]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      // Use the global function we created
      const userList = await window.getUserList();
      setUsers(userList.users || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (window.confirm(`Are you sure you want to delete user ${username}?`)) {
      try {
        await window.removeUser(username);
        alert(`User ${username} has been deleted`);
        loadUsers(); // Refresh the list
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  const handleToggleEnabled = async (user) => {
    const action = user.enabled ? 'disable' : 'enable';
    if (window.confirm(`Are you sure you want to ${action} user ${user.email || user.username}?`)) {
      try {
        await window.setUserEnabled(user.username, !user.enabled);
        loadUsers(); // Refresh the list
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  if (permissionsLoading) {
    return (
      <UmPage embedded={embedded}>
        <header className="um-header">
          <h1 className="um-title">User Management</h1>
        </header>
        <p className="um-empty">Loading…</p>
      </UmPage>
    );
  }

  if (!permissions?.canSeeUserManagement) {
    return (
      <UmPage embedded={embedded}>
        <section className="um-panel um-denied">
          <h2 className="um-panel__title">You can't open User Management</h2>
          <p className="um-muted">It's for RDV Systems administrators. Ask one of them to change an account for you.</p>
        </section>
      </UmPage>
    );
  }

  const q = query.trim().toLowerCase();
  const shownUsers = q ? users.filter((user) => (user.email || '').toLowerCase().includes(q)) : users;
  const notSetUp = users.filter(needsInvitationCode).length;

  return (
    <UmPage embedded={embedded}>
      <header className="um-header">
        <div>
          <h1 className="um-title">User Management</h1>
          <p className="um-summary">
            {users.length === 1 ? '1 account' : `${users.length} accounts`}
            {notSetUp > 0 && `, ${notSetUp} not set up yet`}
          </p>
        </div>
        <button type="button" className="um-btn um-btn--quiet" onClick={loadUsers} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error && <div className="um-error" role="alert">{error}</div>}

      <input
        type="search"
        className="um-search"
        aria-label="Search users"
        placeholder="Search by email or company"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <InvitationCodeNotice invitation={issued} onClose={() => setIssued(null)} />
      <InvitationsPanel version={invitesVersion} onIssued={handleIssued} query={query} />

      <section className="um-panel" aria-labelledby="um-people-title">
        <div className="um-panel__head">
          <h2 id="um-people-title" className="um-panel__title">People</h2>
          {q && <span className="um-count">{shownUsers.length} of {users.length}</span>}
        </div>
        {shownUsers.length === 0 ? (
          <p className="um-empty">
            {q ? 'No one matches your search.' : loading ? 'Loading accounts…' : 'No accounts yet.'}
          </p>
        ) : (
          <div className="um-table-wrap">
            <table className="um-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Groups</th>
                  <th>Status</th>
                  <th className="um-table__actions-head">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownUsers.map((user, index) => {
                  const status = statusOf(user);
                  return (
                    <tr key={user.username || index}>
                      <td><EmailIdentity email={user.email || ''} /></td>
                      <td>
                        {user.groups.length > 0 ? (
                          <div className="um-groups">
                            {user.groups.map((group) => <span key={group} className="um-group">{group}</span>)}
                          </div>
                        ) : (
                          <span className="um-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`um-status um-status--${status.tone}`} title={user.status}>
                          {status.label}
                        </span>
                      </td>
                      <td>
                        <div className="um-actions">
                          {needsInvitationCode(user) && (
                            <PermissionGate permission="manage_users">
                              <button type="button" onClick={() => handleSendCode(user)}
                                className="um-btn um-btn--outline" disabled={sendingCodeTo === user.username}>
                                Send invitation code
                              </button>
                            </PermissionGate>
                          )}
                          {user.status === 'CONFIRMED' && !user.signup_pending && (
                            <PermissionGate permission="manage_users">
                              <button type="button" onClick={() => handleResetCode(user)}
                                className="um-btn um-btn--outline" disabled={sendingCodeTo === user.username}>
                                Password reset code
                              </button>
                            </PermissionGate>
                          )}
                          {/* Managers/Admins can disable or re-enable accounts */}
                          <PermissionGate permission="manage_users">
                            <button type="button" onClick={() => handleToggleEnabled(user)} className="um-btn um-btn--quiet">
                              {user.enabled === false ? 'Enable' : 'Disable'}
                            </button>
                          </PermissionGate>
                          {/* Only admins can delete users */}
                          <PermissionGate permission="delete_users">
                            <button type="button" onClick={() => handleDeleteUser(user.username)}
                              className="um-btn um-btn--danger">
                              Delete
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </UmPage>
  );
};

// The page shell, like the Usage and Safety dashboards: it paints a #050712 ground
// (a shade deeper than their #080b1b) and scrolls itself (.main-content in App.css is black and doesn't scroll).
function UmPage({ children, embedded }) {
  if (embedded) {
    return <div className="user-management user-management--embedded">{children}</div>;
  }
  return (
    <div className="um-page">
      <div className="user-management">{children}</div>
    </div>
  );
}

// Cognito's account states, in words an admin reads at a glance. The raw state stays
// in the tooltip. `tone` picks the dot colour (see .um-status--* in the stylesheet).
const STATUS = {
  CONFIRMED: { label: 'Active', tone: 'ok' },
  FORCE_CHANGE_PASSWORD: { label: "Hasn't signed in", tone: 'wait' },
  RESET_REQUIRED: { label: 'Must reset password', tone: 'wait' },
  UNCONFIRMED: { label: 'Unconfirmed', tone: 'wait' },
};

function statusOf(user) {
  if (user.enabled === false) return { label: 'Disabled', tone: 'off' };
  // `signup_pending` (backend): chose a password, then stopped before the second sign-in
  // step. Cognito calls that CONFIRMED, but the account can't be used yet.
  if (user.signup_pending) return { label: 'Sign-up not finished', tone: 'wait' };
  return STATUS[user.status] || { label: user.status, tone: 'wait' };
}

// Accounts that still need an invitation code to get going: never signed in (from before
// invitation codes), or sign-up started and abandoned. A reset code is refused for both.
function needsInvitationCode(user) {
  return user.status === 'FORCE_CHANGE_PASSWORD' || Boolean(user.signup_pending);
}

export default UserManagement;
